import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAsync } from "../lib/useAsync";
import { Page, Card, PhasePill, ErrorBox, RichLoader, UpdatedAt } from "../components/ui";
import { useForecastPoll } from "../lib/useForecastPoll";
import { useDelayedFlag } from "../lib/useDelayedFlag";
import { useWaitMessage } from "../lib/useWaitMessage";
import { stateMessageKey } from "../lib/loader-copy";
import { Stat } from "../components/Stat";
import { fmtDmg, sinceLabel, agoLabel } from "../lib/format";
import type { CampaignArticle, Candidate, Forecast as ForecastData, Projection } from "@shared/types";
import { wareraArticleUrl } from "@shared/warera-links";
import { candidatePath, forecastShareUrl } from "@shared/share-links";
import { CROWD_FULL_WEIGHT_AT } from "@shared/forecast-model";
import { Share } from "../components/Share";
import { usePageTitle } from "../lib/usePageTitle";
import { useI18n, useT } from "../i18n";
import type { MessageKey } from "../i18n/en";

// Candidate colors — chosen to stay distinct from the ember accent (#f97316)
// and the green success color (#22c55e).
const COLORS = ["#3b82f6", "#a855f7", "#06b6d4", "#ec4899", "#14b8a6"];

export default function Forecast() {
  const t = useT();
  const { electionId = "", countryId = "" } = useParams();
  // One unified poll: GET /forecast returns the data (200) or a warming status
  // (202) carrying the live stage+pct. The hook clamps pct monotonically and
  // ignores the "ready" no-row sentinel (see advancePoll), so no flash/regress.
  const { data, loading, error, pct, stage } = useForecastPoll(electionId);
  const showRich = useDelayedFlag(loading, 400);
  const waitKey = useWaitMessage(loading);
  // Real stage / rate-limit message once a 202 lands; time-escalating wait copy
  // until then.
  const stateKey = stage ? stateMessageKey(stage) : null;
  const loaderMsg = t(stateKey ?? waitKey);
  const loaderPct = pct;
  // Posts are scanned live (slow) — fetched separately so the forecast renders first.
  const posts = useAsync(() => api.articles(electionId), [electionId]);

  // After voting we refetch the forecast — the blend is computed on the backend now.
  const [fresh, setFresh] = useState<ForecastData | null>(null);
  const [myVote, setMyVote] = useState<string | null>(null);
  const [nickname, setNickname] = useState("");
  const [voting, setVoting] = useState(false);

  // On navigation: drop the post-vote copy, then restore this device's prior
  // crowd-poll pick (the cached forecast doesn't carry per-viewer data).
  useEffect(() => {
    setFresh(null);
    setMyVote(null);
    let alive = true;
    api
      .electionPoll(electionId)
      .then((p) => {
        if (alive) setMyVote(p.myVote);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [electionId]);
  const fc = fresh ?? data;
  usePageTitle(
    fc
      ? t("forecast.shareTitle", {
          country: fc.election.countryName,
          office: fc.election.type === "president" ? t("office.presidentTitle") : t("office.congressTitle"),
        })
      : null,
  );

  async function vote(candidateId: string) {
    setVoting(true);
    try {
      await api.vote(electionId, candidateId, nickname || undefined);
      setFresh(await api.forecast(electionId)); // re-blended server-side with the new vote
      setMyVote(candidateId);
    } catch (e) {
      alert(String((e as Error).message));
    } finally {
      setVoting(false);
    }
  }

  return (
    <Page>
      {/* Hardcoded destination, so name it — a bare "Back" reads as history-back. */}
      <Link to={`/c/${countryId}`} className="text-accent text-sm">← {fc ? fc.election.countryName : t("common.back")}</Link>
      {showRich && (
        <RichLoader headline={t("loader.headline.forecast")} message={loaderMsg} pct={loaderPct} />
      )}
      {error && <ErrorBox msg={error} />}
      {fc && (
        <>
          <Header data={fc} />
          <UpdatedAt ms={fc.updatedAt} />
          <Table data={fc} updating={voting} />
          {fc.election.phase === "done" ? (
            <Card className="mb-4">
              <p className="text-muted text-sm">{t("forecast.votingClosed")}</p>
            </Card>
          ) : (
            <PollWidget
              data={fc}
              myVote={myVote}
              voting={voting}
              nickname={nickname}
              setNickname={setNickname}
              onVote={vote}
            />
          )}
          <Stats data={fc} posts={posts.data} postsLoading={posts.loading} />
        </>
      )}
    </Page>
  );
}

function Header({ data }: { data: ForecastData }) {
  const { t, locale } = useI18n();
  const e = data.election;
  return (
    <div className="flex items-center justify-between my-4">
      <div>
        <h1 className="text-xl font-semibold capitalize">
          {e.countryName} — {e.type === "president" ? t("office.president") : t("office.congress")}
        </h1>
        <div className="text-muted text-sm">
          {e.phase === "soon" && t("forecast.votingOpens", { date: new Date(e.votesStartAt).toLocaleString(locale) })}
          {e.phase === "live" && t("forecast.liveTurnout", { pct: turnout(e.votesCount, e.activePopulation) })}
          {e.phase === "done" && t("forecast.closedTurnout", { pct: turnout(e.votesCount, e.activePopulation) })}
        </div>
        <Link
          to={`/c/${e.countryId}/race/${e.type}`}
          state={{ phase: e.phase }}
          className="text-accent text-sm inline-block mt-1"
        >
          {t("forecast.viewCandidates")} →
        </Link>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Share
          title={t("forecast.shareTitle", { country: e.countryName, office: e.type === "president" ? t("office.presidentTitle") : t("office.congressTitle") })}
          text={t("forecast.shareText")}
          url={forecastShareUrl(e.countryId, e.id)}
        />
        <PhasePill phase={e.phase} startsAt={e.votesStartAt} />
      </div>
    </div>
  );
}

function Table({ data, updating }: { data: ForecastData; updating: boolean }) {
  const t = useT();
  const { candidates, projection, election, poll } = data;
  const projByUser = new Map(projection.map((p) => [p.userId, p]));
  const actualTotal = candidates.reduce((s, c) => s + c.voteCount, 0);
  const done = election.phase === "done";
  // Closed elections show the real result only — no forecast.
  const showForecast = !done;
  const showActual = election.phase !== "soon";
  const cols = showForecast ? "grid-cols-[1fr_80px_80px]" : "grid-cols-[1fr_80px]";
  // Crowd influence ramps to full at CROWD_FULL_WEIGHT_AT votes (mirrors blendWithCrowd).
  const crowdWeight = Math.round(Math.min(1, poll.total / CROWD_FULL_WEIGHT_AT) * 100);
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <Card className="mb-4">
      <div className={`grid ${cols} gap-2 text-[11px] uppercase tracking-wide text-muted pb-2.5 border-b border-border`}>
        <span>{t("forecast.candidate")}</span>
        {showForecast && (
          <span className="text-center">{updating ? t("forecast.updating") : t("forecast.forecast")}</span>
        )}
        <span className="text-center">{done ? t("forecast.result") : t("forecast.actual")}</span>
      </div>

      {candidates.map((c, i) => {
        const proj = projByUser.get(c.userId);
        const forecast = proj?.forecastPct ?? 0;
        const actual = actualTotal > 0 ? Math.round((c.voteCount / actualTotal) * 100) : 0;
        const color = COLORS[i % COLORS.length];
        const electedRow = done && c.isElected;
        const isOpen = expanded === c.userId;
        return (
          <div
            key={c.userId}
            className={`border-b border-border last:border-0 ${
              electedRow ? "bg-accent2/10 border-l-2 border-l-accent2 pl-2 -ml-2 rounded-r" : ""
            }`}
          >
            <div className={`grid ${cols} gap-2 items-center py-3`}>
              <div>
                <div className="font-semibold" style={{ color }}>
                  ●{" "}
                  <Link to={candidatePath(c.username)} className="hover:underline">
                    {c.username}
                  </Link>{" "}
                  {c.isIncumbent && <span className="text-xs text-muted">· {t("forecast.incumbent")}</span>}
                </div>
                <div className="text-xs text-muted">
                  {t("forecast.lvlDmg", { lvl: c.level, dmg: fmtDmg(c.careerDamage) })}
                </div>
                {showForecast && proj && (
                  <button
                    type="button"
                    onClick={() => setExpanded(isOpen ? null : c.userId)}
                    className="text-[11px] text-accent hover:underline mt-1"
                  >
                    {isOpen ? "▾" : "▸"} {t("forecast.whyNumbers")}
                  </button>
                )}
              </div>
              {showForecast && (
                <div
                  className={`text-center font-semibold transition-opacity ${updating ? "opacity-40" : ""}`}
                  style={{ color }}
                >{`${forecast}%`}</div>
              )}
              <div className={`text-center font-semibold ${showActual ? "" : "text-muted"}`}>
                {showActual && actualTotal > 0 ? `${actual}%` : "—"}
              </div>
            </div>
            {isOpen && proj && <FactorBreakdown proj={proj} />}
          </div>
        );
      })}

      {showForecast && (
        <div className="text-muted text-[11px] text-center pt-2 space-y-0.5">
          <div>{poll.total < CROWD_FULL_WEIGHT_AT ? t("forecast.blendNote.early") : t("forecast.blendNote.blended")}</div>
          <div>{t("forecast.crowdVotes", { n: poll.total, pct: crowdWeight, full: CROWD_FULL_WEIGHT_AT })}</div>
          <Link to="/how-it-works" className="text-accent hover:underline">
            {t("forecast.howCalculated")}
          </Link>
        </div>
      )}
      {done && election.winners.length > 0 && (
        <div className="text-accent2 font-semibold text-sm pt-2 text-center">
          🏆 {election.winners.length === 1 ? t("forecast.winner") : t("forecast.elected", { n: election.winners.length })}:{" "}
          {election.winners.map((w, idx) => (
            <span key={w.username}>
              {idx > 0 && ", "}
              <Link to={candidatePath(w.username)} className="hover:underline">
                {w.username}
              </Link>
            </span>
          ))}
        </div>
      )}
    </Card>
  );
}

// Per-candidate transparency: the raw model factors that produced this
// candidate's score, before the crowd poll is blended in. Labels are translated
// by factor key; weight/value/points come straight from the live payload.
function FactorBreakdown({ proj }: { proj: Projection }) {
  const t = useT();
  return (
    <div className="bg-panel2/60 rounded-lg px-3 py-2.5 mb-3 -mt-0.5">
      <div className="space-y-1">
        {proj.factors.map((f) => (
          <div key={f.key} className="flex items-baseline justify-between gap-3 text-xs">
            <span className="text-muted">{t(`model.factor.${f.key}.label` as MessageKey)}</span>
            <span className="font-mono text-dim shrink-0">
              {f.weight} × {f.value.toFixed(2)} = <span className="text-ink">{f.points.toFixed(1)}</span>
            </span>
          </div>
        ))}
      </div>
      <div className="border-t border-border mt-2 pt-2 font-mono text-xs text-accent text-right">
        {t("forecast.factorScore", { score: proj.score.toFixed(1), pct: proj.winPct })}
      </div>
    </div>
  );
}

function Stats({
  data,
  posts,
  postsLoading,
}: {
  data: ForecastData;
  posts: Record<string, CampaignArticle[]> | null;
  postsLoading: boolean;
}) {
  const t = useT();
  const hasSnapshots = data.election.phase === "done" && data.candidates.some((c) => c.snapshot);
  const [atElection, setAtElection] = useState(false);
  const useSnap = atElection && hasSnapshots;

  return (
    <Card className="mb-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-sm">{t("forecast.candidateStats")}</h3>
        {hasSnapshots && (
          <button
            onClick={() => setAtElection((v) => !v)}
            className="text-xs text-accent border border-border rounded-full px-3 py-1 hover:border-accent"
          >
            {atElection ? t("forecast.showCurrent") : t("forecast.showAtElection")}
          </button>
        )}
      </div>
      <div className="space-y-3">
        {data.candidates.map((c, i) => {
          const s = useSnap && c.snapshot ? c.snapshot : c;
          return (
            <div key={c.userId} className="border-t border-dashed border-border pt-3 first:border-0 first:pt-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Link
                  to={candidatePath(c.username)}
                  className="font-semibold hover:underline"
                  style={{ color: COLORS[i % COLORS.length] }}
                >
                  {c.username}
                </Link>
                {c.isIncumbent && <Badge>{t("forecast.incumbent")}</Badge>}
                {c.isRulingParty && <Badge>{t("forecast.rulingParty")}</Badge>}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5 text-sm">
                <Stat label={t("stats.level")} value={s.level} />
                <Stat label={t("stats.careerDmg")} value={fmtDmg(s.careerDamage)} sub={s.careerDamageRank ? `#${s.careerDamageRank}` : undefined} />
                <Stat label={t("stats.militaryRank")} value={`#${s.militaryRank}`} />
                <Stat label={t("stats.wealth")} value={fmtDmg(s.wealth)} />
                <Stat label={t("stats.premium")} value={s.premiumMonths ? `${s.premiumMonths}mo` : "—"} />
                <Stat label={t("stats.subscribers")} value={s.subscribers} />
                <Stat label={t("stats.pastElections")} value={c.pastRuns ? `${c.pastWins}W / ${c.pastRuns}` : "—"} />
                <Stat label={t("stats.playingSince")} value={sinceLabel(s.createdAt)} />
                <Stat label={t("stats.lastActive")} value={agoLabel(s.lastConnectionAt, t)} />
              </div>
              <CandidatePosts articles={posts?.[c.userId] ?? c.articles} loading={postsLoading} />
            </div>
          );
        })}
      </div>
      <div className="text-xs text-muted mt-3">
        {useSnap ? t("forecast.statsSnapshot") : t("forecast.statsLive")}
      </div>
    </Card>
  );
}

function CandidatePosts({ articles, loading }: { articles: CampaignArticle[]; loading: boolean }) {
  const t = useT();
  return (
    <div className="mt-3">
      <div className="text-[11px] uppercase tracking-wide text-muted mb-1">{t("forecast.posts")}</div>
      {loading && articles.length === 0 ? (
        <div className="text-sm text-muted">{t("forecast.loadingPosts")}</div>
      ) : articles.length === 0 ? (
        <div className="text-sm text-muted">{t("forecast.noPosts")}</div>
      ) : (
        <div className="space-y-1.5">
          {articles.map((a, idx) => (
            <div key={a.id}>
              <a
                href={wareraArticleUrl(a.id)}
                target="_blank"
                rel="noreferrer"
                className="text-accent text-sm"
              >
                {a.title || t("forecast.untitled")} ↗
              </a>
              {idx === 0 && a.excerpt && <p className="text-muted text-xs mt-0.5">{a.excerpt}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Badge({ children }: { children: React.ReactNode }) {
  return <span className="text-[11px] px-2 py-0.5 rounded-full bg-accent/15 text-blue-300">{children}</span>;
}

function PollWidget({
  data,
  myVote,
  voting,
  nickname,
  setNickname,
  onVote,
}: {
  data: ForecastData;
  myVote: string | null;
  voting: boolean;
  nickname: string;
  setNickname: (s: string) => void;
  onVote: (id: string) => void;
}) {
  const t = useT();
  return (
    <Card className="mb-4">
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <h3 className="font-semibold">{t("forecast.yourVote")}</h3>
        {data.poll.total > 0 && (
          <span className="font-mono text-xs text-muted shrink-0">{t("forecast.pollVotes", { n: data.poll.total })}</span>
        )}
      </div>
      <p className="text-muted text-sm mb-3">{t("forecast.whoVote")}</p>
      <input
        className="w-full bg-panel2 border border-border rounded-lg px-3 py-2 text-sm mb-3 outline-none"
        placeholder={t("forecast.nicknamePlaceholder")}
        value={nickname}
        maxLength={24}
        onChange={(e) => setNickname(e.target.value)}
      />
      <div className="flex gap-2.5 flex-wrap">
        {data.candidates.map((c: Candidate) => (
          <button
            key={c.userId}
            disabled={voting}
            onClick={() => onVote(c.userId)}
            className={`flex-1 min-w-[120px] rounded-lg p-3 font-semibold border transition disabled:opacity-50 ${
              myVote === c.userId ? "border-accent bg-accent/15" : "border-border bg-panel2 hover:border-accent"
            }`}
          >
            {c.username}
          </button>
        ))}
      </div>
      {myVote && (
        <div className="text-accent2 text-sm mt-3">
          {t("forecast.voteRecorded")}
        </div>
      )}
    </Card>
  );
}

const turnout = (v: number, pop: number) => (pop > 0 ? Math.round((v / pop) * 100) : 0);
